/**
 * AI Context - Backend Version
 *
 * Replaces LangChain frontend implementation with FastAPI backend calls.
 * All AI logic runs on the backend using PydanticAI.
 */
import { aiLogger, autoSuggestionsLogger } from '@annote/core/utils/debug';
import type { UUID } from '@annote/types';
import {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
  MutableRefObject,
} from 'react';
import { createUUID } from '@annote/core/utils/notes';
import { sendMessage } from 'src/api/annie';
import { supabaseClient } from 'src/db/supabase';
import { dismissSuggestionGroup, acceptSuggestionGroup } from 'src/api/suggestions';
import { ShownIdsContext } from './ShownContext';
// eslint-disable-next-line import/no-cycle
import { SuggestedContext } from './SuggestedContext';
import { FocusContext } from './FocusContext';
import { useSettings } from './SettingsContext';

export const useLatest = <T,>(value: T): MutableRefObject<T> => {
  const ref = useRef<T>(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
};

interface Message {
  role: 'user' | 'assistant' | 'step' | 'thinking' | 'error' | '$';
  content: string;
}

interface AIContextType {
  messages: Message[];
  currentStreamingMessage: string;
  currentStatusMessage: string;
  isLoading: boolean;
  error: string | null;
  input: string;
  isMinimized: boolean;
  showSteps: boolean;
  thread: UUID;
  availableSuggestionGroups: Array<{ id: UUID; title: string; group_type: string }>;
  isRateLimited: boolean;
  setInput: (input: string) => void;
  setIsMinimized: (isMinimized: boolean) => void;
  setShowSteps: (showSteps: boolean) => void;
  startNewThread: () => void;
  submitMessage: (message: string) => Promise<void>;
  clearMessages: () => void;
  applySuggestionGroup: (groupId: UUID) => Promise<void>;
  dismissSuggestionGroupFromBar: (groupId: UUID) => Promise<void>;
}

// Export the context so other modules can use useContext directly to avoid circular dependencies
export const AIContext = createContext<AIContextType | null>(null);

interface AIProviderProps {
  children: ReactNode;
}

export const AIProvider: React.FC<AIProviderProps> = ({ children }) => {
  const shown = useContext(ShownIdsContext);
  // Use SuggestedContext directly to avoid circular dependency with useSuggestedContext hook
  const suggested = useContext(SuggestedContext);
  const { focusedNoteId } = useContext(FocusContext);
  const { getSetting } = useSettings();

  const shownRef = useLatest(shown);
  const focusedDocumentIdRef = useLatest(focusedNoteId);
  const rateLimitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Messages are ephemeral - cleared on page refresh
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState('');
  const [currentStatusMessage, setCurrentStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isMinimized, setIsMinimized] = useState(true);
  const [showSteps] = useState(true); // Keep for backwards compatibility
  const [thread, setThread] = useState<UUID>(createUUID());
  const [availableSuggestionGroups, setAvailableSuggestionGroups] = useState<
    Array<{ id: UUID; title: string; group_type: string }>
  >([]);
  const [isRateLimited, setIsRateLimited] = useState(false);

  // Cleanup rate limit timeout on unmount
  useEffect(() => {
    return () => {
      if (rateLimitTimeoutRef.current) {
        clearTimeout(rateLimitTimeoutRef.current);
      }
    };
  }, []);

  const startNewThread = useCallback(() => {
    setThread(createUUID());
    setMessages([]);
    try {
      localStorage.removeItem('annie-messages');
    } catch (clearError) {
      aiLogger.error('Failed to clear persisted messages:', clearError);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setCurrentStreamingMessage('');
    setCurrentStatusMessage('');
    setError(null);
    setIsRateLimited(false);
    // Clear any pending rate limit timeout
    if (rateLimitTimeoutRef.current) {
      clearTimeout(rateLimitTimeoutRef.current);
      rateLimitTimeoutRef.current = null;
    }
    try {
      localStorage.removeItem('annie-messages');
    } catch (clearError) {
      aiLogger.error('Failed to clear persisted messages:', clearError);
    }
  }, []);

  // Apply a suggestion group - called when user clicks on an auto-suggestion
  const applySuggestionGroup = useCallback(
    async (groupId: UUID): Promise<void> => {
      console.log('[applySuggestionGroup] Called with groupId:', groupId);
      autoSuggestionsLogger.debug('Applying group:', groupId);
      autoSuggestionsLogger.debug(
        'Available groups:',
        availableSuggestionGroups.map((g) => g.id),
      );
      autoSuggestionsLogger.debug(
        'Group exists in available groups?',
        availableSuggestionGroups.some((g) => g.id === groupId),
      );

      // Process the suggestion group through SuggestedContext FIRST
      // This creates notes/links before marking as accepted
      // We do this BEFORE removing from UI state to ensure we have the group data
      if (suggested) {
        try {
          autoSuggestionsLogger.debug('Calling addSuggestionGroups with groupId:', groupId);
          await suggested.addSuggestionGroups([groupId]);
          autoSuggestionsLogger.debug('Successfully added suggestion group:', groupId);
        } catch (addError) {
          autoSuggestionsLogger.error('Failed to add suggestion group:', addError);
          aiLogger.error('Failed to add suggestion group:', addError);
          // Don't remove from UI if processing failed
          throw addError;
        }
      } else {
        autoSuggestionsLogger.error('SuggestedContext not available');
        throw new Error('SuggestedContext not available');
      }

      // Remove from available suggestions after successful processing
      setAvailableSuggestionGroups((prev) => prev.filter((g) => g.id !== groupId));

      // Mark suggestions as accepted in database AFTER processing
      try {
        await acceptSuggestionGroup(groupId);
      } catch (acceptError) {
        aiLogger.error('Failed to accept suggestion group:', acceptError);
        // Continue even if DB update fails - notes are already created
      }
    },
    [suggested],
  );

  // Dismiss a suggestion group - called when user clicks X on an auto-suggestion
  const dismissSuggestionGroupFromBar = useCallback(async (groupId: UUID): Promise<void> => {
    // Remove from available suggestions
    setAvailableSuggestionGroups((prev) => prev.filter((g) => g.id !== groupId));
    // Mark as dismissed in database
    await dismissSuggestionGroup(groupId);
  }, []);

  // Fetch auto-suggestions function (extracted for reuse)
  const fetchAutoSuggestions = useCallback(async (): Promise<void> => {
    autoSuggestionsLogger.debug('Fetching suggestions', {
      shownIdsLength: shown.shownIds.length,
      isLoading,
      messagesLength: messages.length,
    });

    if (shown.shownIds.length === 0) {
      autoSuggestionsLogger.debug('Canvas is blank, fetching suggestions...');
      try {
        // Get current user ID - explicitly filter by userId for reliability
        const {
          data: { user },
        } = await supabaseClient.auth.getUser();
        if (!user) {
          autoSuggestionsLogger.debug('No authenticated user, skipping fetch');
          return;
        }

        // Query Supabase directly for pending auto-suggestions
        autoSuggestionsLogger.debug('Querying suggestion_groups table with filters:', {
          userId: user.id,
          status: 'pending',
          trigger_type: 'batch_analysis',
          expires_at: null,
        });

        const { data: suggestionGroups, error: queryError } = await supabaseClient
          .from('suggestion_groups')
          .select('id, title, group_type')
          .eq('userId', user.id)
          .eq('status', 'pending')
          .eq('trigger_type', 'batch_analysis')
          .is('expires_at', null)
          .order('priority', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(10);

        if (queryError) {
          autoSuggestionsLogger.error('Error fetching suggestions:', queryError);
          aiLogger.error('Failed to fetch auto-suggestions:', queryError);
          return;
        }

        autoSuggestionsLogger.debug('Query returned:', {
          count: suggestionGroups?.length ?? 0,
          groups: suggestionGroups,
        });

        if (suggestionGroups && suggestionGroups.length > 0) {
          autoSuggestionsLogger.debug('Setting available suggestion groups:', suggestionGroups);
          // Store available suggestion groups for display (don't process yet)
          setAvailableSuggestionGroups(suggestionGroups);

          // Expand Annie drawer to show suggestions
          const shouldExpand = !isLoading && messages.length === 0;
          autoSuggestionsLogger.debug('Should expand Annie drawer?', {
            shouldExpand,
            isLoading,
            messagesLength: messages.length,
          });
          if (shouldExpand) {
            setIsMinimized(false);
          }
        } else {
          autoSuggestionsLogger.debug('No suggestions returned from query');
        }
      } catch (fetchError) {
        autoSuggestionsLogger.error('Exception fetching suggestions:', fetchError);
        aiLogger.error('Failed to fetch auto-suggestions:', fetchError);
      }
    } else {
      autoSuggestionsLogger.debug('Canvas has notes, clearing suggestions', {
        shownIdsLength: shown.shownIds.length,
      });
      // Clear auto-suggestions when notes are shown
      setAvailableSuggestionGroups([]);
    }
  }, [shown.shownIds.length, isLoading, messages.length]);

  // Event-based auto-suggestions: fetch when canvas becomes blank (shownIds.length changes to 0)
  useEffect(() => {
    // When canvas has notes, clear suggestions
    if (shown.shownIds.length > 0) {
      // Clear suggestions when canvas has notes
      fetchAutoSuggestions(); // This will clear suggestions since shownIds.length > 0
      return;
    }

    // Canvas is blank - fetch suggestions immediately (event-based trigger)
    fetchAutoSuggestions();
  }, [shown.shownIds.length, isLoading, fetchAutoSuggestions]);

  const submitMessage = useCallback(
    async (message: string): Promise<void> => {
      setIsMinimized(false);
      if (!message.trim()) return;

      // Build context object for backend
      // Note: toolSettings are user preference toggles (feature flags), not tool definitions.
      // The backend has many tools - these settings control whether certain tool categories are enabled.
      const context = {
        shownIds: shownRef.current.shownIds,
        focusedDocumentId: focusedDocumentIdRef.current,
        annieContext: getSetting<string>('annie.annieContext'),
        organizeContext: getSetting<string>('annie.organizeContext'),
        toolSettings: {
          findRelatedDocuments: getSetting<boolean>('annie.tools.findRelatedDocuments'),
          enableWebResearch: getSetting<boolean>('annie.tools.enableWebResearch'),
          // makeSuggestions is defined but not yet implemented in backend tools
        },
      };

      // Add user message to UI
      setMessages((prev) => [...prev, { role: 'user', content: message }]);
      setIsLoading(true);
      setCurrentStreamingMessage('');
      setCurrentStatusMessage(''); // Will be replaced by streaming reasoning from GPT-5.1
      setError(null); // Clear any previous errors

      // Accumulate streaming tokens in a local variable to avoid closure issues
      let accumulatedStreamingMessage = '';

      try {
        // Call backend API with streaming callbacks
        const response = await sendMessage(
          message,
          {
            onToken: (token: string) => {
              accumulatedStreamingMessage += token;
              setCurrentStreamingMessage(accumulatedStreamingMessage);
            },
            onStatus: (status: string) => {
              setCurrentStatusMessage(status);
            },
            onError: (errorMessage: string) => {
              const errorText = `Error: ${errorMessage}`;
              setError(errorText);
              setMessages((prev) => [...prev, { role: 'error', content: errorText }]);
            },
          },
          context,
        );

        // Add final message to chat - prioritize response.status (brief status from backend)
        // but fall back to accumulated streaming message if response.status is empty
        const finalMessage = response.status || accumulatedStreamingMessage;

        // Add message to array first
        setMessages((prev) => [...prev, { role: 'assistant', content: finalMessage }]);

        // Handle UI actions
        if (response.ui_actions.show_document_ids.length > 0) {
          shown.show(response.ui_actions.show_document_ids);
        }

        if (response.ui_actions.hide_document_ids.length > 0) {
          shown.hide(response.ui_actions.hide_document_ids);
        }

        // Hand off suggestion group IDs to SuggestedContext for processing
        aiLogger.debug('Received suggestion_group_ids:', response.suggestion_group_ids);
        if (response.suggestion_group_ids.length > 0 && suggested) {
          try {
            await suggested.addSuggestionGroups(response.suggestion_group_ids);
          } catch (suggestionError) {
            // Log error but don't break the chat flow - suggestions simply won't appear
            aiLogger.error('Error fetching suggestion groups (non-fatal):', suggestionError);
          }
        } else {
          aiLogger.debug('No suggestion groups received from backend or SuggestedContext not available');
        }

        // Clear streaming state after message is added to array
        // React will batch these updates, so displayedMessage in AnnIChat will
        // fall back to latestAssistantMessage?.content when currentStreamingMessage is cleared
        setCurrentStreamingMessage('');
        setCurrentStatusMessage('');
        setError(null); // Clear error on success
        setIsRateLimited(false); // Clear rate limit on success
      } catch (chatError) {
        aiLogger.error('Error in chat:', chatError);
        const errorMessage = chatError instanceof Error ? chatError.message : String(chatError);

        // Check if this is a rate limit error (429 status)
        const isRateLimitError = (chatError as Error & { status?: number }).status === 429;

        if (isRateLimitError) {
          setIsRateLimited(true);
          const rateLimitText = 'Rate limit exceeded. Please try again in a moment.';
          setError(rateLimitText);
          setMessages((prev) => [...prev, { role: 'error', content: rateLimitText }]);

          // Clear any existing timeout
          if (rateLimitTimeoutRef.current) {
            clearTimeout(rateLimitTimeoutRef.current);
          }

          // Clear rate limit after 60 seconds
          rateLimitTimeoutRef.current = setTimeout(() => {
            setIsRateLimited(false);
            setError(null);
            rateLimitTimeoutRef.current = null;
          }, 60000);
        } else {
          // Error message is already sanitized by the API client
          // Remove "Error in chat: " prefix if present (it's added here but might be redundant)
          let errorText = errorMessage;
          if (errorMessage.startsWith('Error in chat: ')) {
            errorText = errorMessage.slice('Error in chat: '.length);
          } else if (errorMessage.startsWith('Chat failed: ')) {
            errorText = errorMessage.slice('Chat failed: '.length);
          }
          setError(errorText);
          setMessages((prev) => [...prev, { role: 'error', content: errorText }]);
        }

        // Clear streaming state on error
        setCurrentStreamingMessage('');
        setCurrentStatusMessage('');
      } finally {
        setIsLoading(false);
        setInput('');
      }
    },
    [shownRef, focusedDocumentIdRef, getSetting, suggested],
  );

  const value: AIContextType = useMemo(
    () => ({
      messages,
      currentStreamingMessage,
      currentStatusMessage,
      isLoading,
      error,
      input,
      isMinimized,
      showSteps,
      thread,
      availableSuggestionGroups,
      isRateLimited,
      setInput,
      setIsMinimized,
      setShowSteps: () => {}, // No-op for backwards compatibility
      startNewThread,
      submitMessage,
      clearMessages,
      applySuggestionGroup,
      dismissSuggestionGroupFromBar,
    }),
    [
      messages,
      currentStreamingMessage,
      currentStatusMessage,
      isLoading,
      error,
      input,
      isMinimized,
      showSteps,
      thread,
      availableSuggestionGroups,
      isRateLimited,
      setInput,
      setIsMinimized,
      startNewThread,
      submitMessage,
      clearMessages,
      applySuggestionGroup,
      dismissSuggestionGroupFromBar,
    ],
  );

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
};

export const useAIContext = (): AIContextType => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAIContext must be used within an AIProvider');
  }
  return context;
};
